'use client';

import React, { useEffect, useRef } from 'react';
import { Reply, Smile, Copy, Forward, Trash2, CheckSquare, Share2, Pencil, Pin } from 'lucide-react';
import { ContextMenuState } from './types';

interface ContextMenuProps {
  state: ContextMenuState;
  messageId: string;
  isMine?: boolean;
  onAction: (action: string, msgId: string) => void;
  onClose: () => void;
}

const ACTIONS = [
  { key: 'reply', label: 'Reply', icon: Reply },
  { key: 'react', label: 'React', icon: Smile },
  { key: 'copy', label: 'Copy Text', icon: Copy },
  { key: 'edit', label: 'Edit', icon: Pencil, mineOnly: true },
  { key: 'pin', label: 'Pin Message', icon: Pin },
  { key: 'forward', label: 'Forward', icon: Forward },
  { key: 'share', label: 'Share', icon: Share2 },
  { key: 'select', label: 'Select', icon: CheckSquare },
  { key: 'delete', label: 'Delete', icon: Trash2, danger: true },
];

export default function ContextMenu({ state, messageId, isMine, onAction, onClose }: ContextMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);

  const menuWidth = 180;
  const menuHeight = ACTIONS.length * 44 + 8;
  const clampedX = Math.min(state.x, (typeof window !== 'undefined' ? window.innerWidth : 400) - menuWidth - 8);
  const clampedY = Math.min(state.y, (typeof window !== 'undefined' ? window.innerHeight : 600) - menuHeight - 8);

  const visibleActions = isMine
    ? ACTIONS
    : ACTIONS.filter(a => a.key !== 'delete' && !a.mineOnly);

  return (
    <div
      ref={menuRef}
      className="fixed z-50 rounded-xl py-1 min-w-[180px] animate-scale-in"
      style={{
        left: `${clampedX}px`,
        top: `${clampedY}px`,
        background: '#1E2E40',
        border: '1px solid rgba(255,255,255,0.08)',
        boxShadow: '0 12px 40px rgba(0,0,0,0.6)',
      }}
      role="menu"
      aria-label="Message actions"
    >
      {visibleActions.map((action) => {
        const ActionIcon = action.icon;
        return (
          <button
            key={`ctx-${action.key}`}
            onClick={() => onAction(action.key, messageId)}
            className="w-full flex items-center gap-3 px-4 py-2.5 text-[13.5px] font-medium transition-colors duration-100 focus:outline-none"
            style={{
              color: action.danger ? '#FF6B6B' : '#FFFFFF',
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLButtonElement).style.background = action.danger ? 'rgba(255,107,107,0.1)' : 'rgba(255,255,255,0.07)';
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
            }}
            role="menuitem"
            aria-label={action.label}
          >
            <ActionIcon size={15} className="flex-shrink-0" />
            {action.label}
          </button>
        );
      })}
    </div>
  );
}